import { useRef, useState } from "react";
import { Eye, Plus } from "lucide-react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";

import {
  type CarrierOperator,
  useDeleteCarrierOperator,
  useCarrierOperator,
  useCarrierOperators,
  useResetOperatorPassword,
  useUpdateOperator,
} from "@/api/carrier";
import {
  EmptyRow,
  ErrorRow,
  LoadingRows,
  TablePager,
  TableToolbar,
  usePagedState,
} from "@/components/data-table";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { RowMenu, RowMenuItem } from "@/components/ui/row-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreateOperatorDialog } from "@/features/create-operator-dialog";
import { EditOperatorDialog } from "@/features/edit-operator-dialog";
import { OperatorDetailDialog } from "@/features/operator-detail-dialog";
import { hasHttpStatus } from "@/lib/api-error";
import { operatorDisplayName, operatorInitials } from "@/lib/operator-identity";

/**
 * Operators screen — carrier-portal P3.8. Pagination/status/search are
 * server-backed, with detail and account actions scoped by the API.
 */
const STATUS_FILTERS = ["ON_DUTY", "OFF_DUTY", "BLOCKED"] as const;
const COLUMNS = 6;

function operatorDispatchCenterName(op: CarrierOperator): string {
  return op.dispatchCenter?.name ?? op.depot?.name ?? "Unknown Dispatch Center";
}

function OperatorActions({
  operator,
  label,
  onEdit,
  onDetails,
  onResetPassword,
  onBlockToggle,
  onDelete,
  disabled = false,
}: {
  operator: CarrierOperator;
  label?: string;
  onEdit: () => void;
  onDetails: () => void;
  onResetPassword: () => void;
  onBlockToggle: () => void;
  onDelete: () => void;
  disabled?: boolean;
}) {
  return (
    <RowMenu label={label} disabled={disabled}>
      <RowMenuItem disabled={disabled} onClick={onEdit}>
        Edit
      </RowMenuItem>
      <RowMenuItem disabled={disabled} onClick={onDetails}>
        Details
      </RowMenuItem>
      <RowMenuItem disabled={disabled} onClick={onResetPassword}>
        Reset password
      </RowMenuItem>
      <RowMenuItem disabled={disabled} onClick={onBlockToggle}>
        {operator.status === "BLOCKED" ? "Unblock" : "Block"}
      </RowMenuItem>
      <RowMenuItem disabled={disabled} variant="destructive" onClick={onDelete}>
        Delete
      </RowMenuItem>
    </RowMenu>
  );
}

export default function OperatorsScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedOperatorId = searchParams.get("operatorId");
  const [status, setStatus] = useState<string>("");
  const { page, pageSize, search, setPage, setPageSize, setSearch, resetPage } =
    usePagedState({ initialSearch: searchParams.get("q") ?? "" });
  const { data, isLoading, error, refetch } = useCarrierOperators({
    page,
    limit: pageSize,
    q: search,
    ...(status && { status }),
  });
  const linkedOperator = useCarrierOperator(linkedOperatorId);
  const updateOperator = useUpdateOperator();
  const resetPassword = useResetOperatorPassword();
  const deleteOperator = useDeleteCarrierOperator();
  const actionInFlight = useRef(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState("");
  const [editing, setEditing] = useState<CarrierOperator | null>(null);
  const [details, setDetails] = useState<CarrierOperator | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CarrierOperator | null>(
    null
  );
  const [creating, setCreating] = useState(false);
  const rows = data?.data ?? [];
  const visibleDetails =
    details ??
    (linkedOperatorId ? linkedOperator.data : null) ??
    rows.find((operator) => operator.id === linkedOperatorId) ??
    null;
  const actionsPending =
    pendingAction !== null ||
    updateOperator.isPending ||
    resetPassword.isPending ||
    deleteOperator.isPending;

  const startAction = (key: string, message: string) => {
    if (actionInFlight.current) return false;
    actionInFlight.current = true;
    setPendingAction(key);
    setPendingMessage(message);
    return true;
  };

  const finishAction = () => {
    actionInFlight.current = false;
    setPendingAction(null);
    setPendingMessage("");
  };

  const handleBlockToggle = async (op: CarrierOperator) => {
    const nextStatus = op.status === "BLOCKED" ? "OFF_DUTY" : "BLOCKED";
    if (
      !startAction(
        `status:${op.id}`,
        `${nextStatus === "BLOCKED" ? "Blocking" : "Unblocking"} ${operatorDisplayName(op)}...`
      )
    )
      return;
    try {
      await updateOperator.mutateAsync({
        operatorId: op.id,
        body: { status: nextStatus },
      });
      toast.success(
        nextStatus === "BLOCKED" ? "Operator blocked." : "Operator unblocked."
      );
    } catch (mutationError) {
      toast.error(
        hasHttpStatus(mutationError, 403)
          ? "You do not have permission to update operator status."
          : "Failed to update operator status."
      );
    } finally {
      finishAction();
    }
  };

  const handleResetPassword = async (op: CarrierOperator) => {
    if (
      !startAction(
        `reset:${op.id}`,
        `Sending password reset for ${operatorDisplayName(op)}...`
      )
    )
      return;
    try {
      const result = await resetPassword.mutateAsync(op.id);
      toast.success(
        result.mode === "invite"
          ? "Operator invite resent."
          : "Password reset email sent."
      );
    } catch (mutationError) {
      toast.error(
        hasHttpStatus(mutationError, 403)
          ? "You do not have permission to reset this operator's password."
          : "Failed to send reset email."
      );
    } finally {
      finishAction();
    }
  };

  const handleDelete = async (op: CarrierOperator) => {
    if (
      !startAction(`delete:${op.id}`, `Deleting ${operatorDisplayName(op)}...`)
    )
      return;
    try {
      await deleteOperator.mutateAsync(op.id);
      toast.success("Operator deleted.");
    } catch (mutationError) {
      // Surface the server's reason (e.g. "Cannot delete operator with active
      // tasks or trips") instead of a blind generic — mirrors the invite
      // dialog (#107).
      const response = mutationError as {
        response?: { data?: { error?: string } };
      };
      toast.error(
        hasHttpStatus(mutationError, 403)
          ? "You do not have permission to delete this operator."
          : (response.response?.data?.error ?? "Failed to delete operator.")
      );
    } finally {
      finishAction();
    }
  };

  const showRows = !isLoading && !error;
  const forbidden = hasHttpStatus(error, 403);
  const emptyMessage =
    search || status ? "No operators match your filters." : "No operators yet.";

  return (
    <div className="carrier-page">
      <PageHeader
        title="Operators"
        actions={
          <Button size="touch" onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Invite operator
          </Button>
        }
      />

      {pendingMessage && (
        <p role="status" className="sr-only">
          {pendingMessage}
        </p>
      )}

      <Card className="overflow-hidden">
        <div className="border-b p-4">
          <TableToolbar
            className="mb-0"
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search operators..."
            searchAriaLabel="Search operators"
          >
            <Select
              value={status || "ALL"}
              onValueChange={(value) => {
                resetPage();
                setStatus(value === "ALL" ? "" : value);
              }}
            >
              <SelectTrigger
                aria-label="Filter by status"
                className="w-full sm:w-44"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                {STATUS_FILTERS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TableToolbar>
        </div>

        <div className="md:hidden">
          {isLoading && (
            <div
              role="status"
              className="p-6 text-center text-sm text-zinc-500"
            >
              Loading operators...
            </div>
          )}
          {error && (
            <div role="alert" className="space-y-3 p-6 text-center">
              <p className="text-sm text-destructive">
                {forbidden
                  ? "You do not have permission to view operators."
                  : "Failed to load operators."}
              </p>
              {!forbidden && (
                <Button
                  variant="outline"
                  size="sm-touch"
                  onClick={() => refetch()}
                >
                  Retry
                </Button>
              )}
            </div>
          )}
          {showRows && rows.length === 0 && (
            <p className="p-6 text-center text-sm text-zinc-500">
              {emptyMessage}
            </p>
          )}
          {showRows && rows.length > 0 && (
            <ul aria-label="Operator summaries" className="divide-y">
              {rows.map((op) => {
                const name = operatorDisplayName(op);
                const vehicle = op.vehicle
                  ? (op.vehicle.licensePlate ?? op.vehicle.code)
                  : "No vehicle assigned";
                return (
                  <li key={op.id} aria-label={name} className="p-3">
                    <div className="flex items-start gap-2.5">
                      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-semibold text-indigo-700">
                        {operatorInitials(op)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{name}</p>
                        <p className="truncate text-[11px] text-zinc-500">
                          {op.carrierUser?.email ?? "No email"}
                        </p>
                      </div>
                      <StatusBadge status={op.status} />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-touch"
                        aria-label={`View ${name} details`}
                        onClick={() => setDetails(op)}
                      >
                        <Eye />
                      </Button>
                      <OperatorActions
                        operator={op}
                        label={`Operator menu for ${name}`}
                        onEdit={() => setEditing(op)}
                        onDetails={() => setDetails(op)}
                        onResetPassword={() => void handleResetPassword(op)}
                        onBlockToggle={() => void handleBlockToggle(op)}
                        onDelete={() => setDeleteTarget(op)}
                        disabled={actionsPending}
                      />
                    </div>
                    <dl className="mt-2 grid grid-cols-3 gap-x-3 text-xs">
                      <div className="min-w-0">
                        <dt className="text-[10px] uppercase text-zinc-400">
                          Phone
                        </dt>
                        <dd className="truncate">
                          {op.carrierUser?.phone ?? "No phone"}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-[10px] uppercase text-zinc-400">
                          Center
                        </dt>
                        <dd className="truncate">
                          {operatorDispatchCenterName(op)}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-[10px] uppercase text-zinc-400">
                          Vehicle
                        </dt>
                        <dd className="truncate">{vehicle}</dd>
                      </div>
                    </dl>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Name</TableHead>
                <TableHead className="whitespace-nowrap">Phone</TableHead>
                <TableHead className="whitespace-nowrap">
                  Dispatch center
                </TableHead>
                <TableHead className="whitespace-nowrap">Vehicle</TableHead>
                <TableHead className="whitespace-nowrap">Status</TableHead>
                <TableHead className="w-12" aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <LoadingRows columns={COLUMNS} label="Loading operators..." />
              )}
              {error && (
                <ErrorRow
                  columns={COLUMNS}
                  message={
                    forbidden
                      ? "You do not have permission to view operators."
                      : "Failed to load operators."
                  }
                  onRetry={forbidden ? undefined : () => refetch()}
                />
              )}
              {showRows && rows.length === 0 && (
                <EmptyRow columns={COLUMNS}>{emptyMessage}</EmptyRow>
              )}
              {showRows &&
                rows.map((op) => {
                  const name = operatorDisplayName(op);
                  const vehicle = op.vehicle
                    ? (op.vehicle.licensePlate ?? op.vehicle.code)
                    : "No vehicle assigned";
                  return (
                    <TableRow key={op.id}>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-semibold text-indigo-700">
                            {operatorInitials(op)}
                          </span>
                          <span className="min-w-0 font-medium">
                            {name}
                            {op.carrierUser?.email && (
                              <span className="block truncate text-[11px] font-normal text-zinc-400">
                                {op.carrierUser.email}
                              </span>
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {op.carrierUser?.phone ?? "No phone"}
                      </TableCell>
                      <TableCell>{operatorDispatchCenterName(op)}</TableCell>
                      <TableCell>{vehicle}</TableCell>
                      <TableCell>
                        <StatusBadge status={op.status} />
                      </TableCell>
                      <TableCell>
                        <OperatorActions
                          operator={op}
                          onEdit={() => setEditing(op)}
                          onDetails={() => setDetails(op)}
                          onResetPassword={() => void handleResetPassword(op)}
                          onBlockToggle={() => void handleBlockToggle(op)}
                          onDelete={() => setDeleteTarget(op)}
                          disabled={actionsPending}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>

        {data && (
          <div className="border-t p-3">
            <TablePager
              page={data.metadata.page}
              pageSize={data.metadata.limit}
              total={data.metadata.total}
              totalPages={data.metadata.totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          </div>
        )}
      </Card>

      {editing && (
        <EditOperatorDialog
          open={!!editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          operator={editing}
        />
      )}
      <OperatorDetailDialog
        operator={visibleDetails}
        onOpenChange={(open) => {
          if (!open) {
            setDetails(null);
            if (searchParams.has("operatorId")) {
              const next = new URLSearchParams(searchParams);
              next.delete("operatorId");
              setSearchParams(next, { replace: true });
            }
          }
        }}
      />
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete operator?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `${operatorDisplayName(deleteTarget)} will be removed from this carrier. This action cannot be undone.`
                : "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionsPending}>
              Keep operator
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={actionsPending}
              onClick={() => {
                if (deleteTarget) void handleDelete(deleteTarget);
              }}
            >
              Delete operator
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {creating && (
        <CreateOperatorDialog open={creating} onOpenChange={setCreating} />
      )}
    </div>
  );
}
